import { PcbLayoutFootprintPadShape, PcbLayoutShape, } from "@buildwithflux/models";
import { Vector2 } from "three";
import { cache } from "../cache";
// eslint-disable-next-line boundaries/element-types
import { Clipper, JoinType } from "../clipper";
import { createShapeFromDxfString } from "./dxf/createShapeFromDxfString";
import { isDxfFileName } from "./helpers/isDxfFileName";
import { isSvgFileName } from "./helpers/isSvgFileName";
import { scaleFromUnit } from "./helpers/scaleShapeBasedOnUnitRule";
import { createCircleOrOblongShape } from "./primitives/createCircleOrOblongShape";
import { createCircularShape } from "./primitives/createCircularShape";
import { createFluxShape } from "./primitives/createFluxShape";
import { createRectangularShape } from "./primitives/createRectangularShape";
import { createShapeFromSvgString } from "./svg/createShapeFromSvgString";
import { createSvgShape } from "./svg/createSvgShape";
export function createShapeFromAsset(bakedRules, expansion, options = {}) {
    // Important: Only svg shapes (and possibly other externally-defined shapes such as DXF, though that's
    // not implemented) should be scaled by the unit rule. Other values (eg rectangle size) are already
    // correctly unit-converted in the rules parsing/evaluation phase.
    return cache(() => {
        const scaleUnit = scaleFromUnit(bakedRules.unit);
        const scale = new Vector2(bakedRules.scale?.x ?? 1, bakedRules.scale?.y ?? 1);
        // If valid asset data is contained in bakedRules then attempt shape creation from that data - asset data takes
        // precedence in all cases
        if (bakedRules.asset?.payloadFileName && bakedRules.asset?.payloadAsBase64) {
            if (isSvgFileName(bakedRules.asset.payloadFileName)) {
                try {
                    return createShapeFromSvgString(atob(bakedRules.asset.payloadAsBase64), {
                        expansion,
                        scale: scale.multiplyScalar(scaleUnit),
                        polygonSegments: options.polygonSegments,
                    });
                }
                catch (e) {
                    if (options.logger && "captureError" in options.logger) {
                        options.logger?.captureError(e, {
                            notifyUserMessage: "Failed to create shape from SVG string.",
                        });
                    }
                }
            }
            if (isDxfFileName(bakedRules.asset.payloadFileName)) {
                try {
                    return createShapeFromDxfString(atob(bakedRules.asset.payloadAsBase64), {
                        expansion,
                        scale,
                        center: false,
                    });
                }
                catch (e) {
                    if (options.logger && "captureError" in options.logger) {
                        options.logger?.captureError(e, {
                            notifyUserMessage: "Failed to create shape from DXF string.",
                        });
                    }
                }
            }
        }
        return undefined;
    }, createShapeFromAsset.name, [
        expansion,
        bakedRules.unit,
        bakedRules.scale?.x, // TODO: fix, typing tells us scale *is* defined
        bakedRules.scale?.y,
        bakedRules.asset?.payloadAsBase64,
        bakedRules.asset?.payloadFileName,
        options.polygonSegments,
    ]);
}
export function createShapeFromPadShape(bakedRules, expansion) {
    return cache(() => {
        const scaleUnit = scaleFromUnit(bakedRules.unit);
        const scale = new Vector2(bakedRules.scale?.x ?? 1, bakedRules.scale?.y ?? 1);
        if ("padShape" in bakedRules && bakedRules.padShape) {
            switch (bakedRules.padShape) {
                case PcbLayoutFootprintPadShape.circular: {
                    return createCircleOrOblongShape(bakedRules.size, { x: 0, y: 0 }, 0);
                }
                case PcbLayoutFootprintPadShape.rectangle: {
                    const shape = createRectangularShape(-bakedRules.size.x / 2, -bakedRules.size.y / 2, bakedRules.size.x, bakedRules.size.y, bakedRules.cornerRadius);
                    if (expansion !== 0) {
                        return Clipper.fromShape(shape, { closed: true })
                            .offset(expansion, {
                            joinType: JoinType.Round,
                        })
                            .toShape(0);
                    }
                    return shape;
                }
                default: {
                    return createSvgShape(bakedRules.padShape, undefined, { scale: scale.multiplyScalar(scaleUnit) });
                }
            }
        }
        return undefined;
    }, createShapeFromPadShape.name, [
        bakedRules.unit,
        bakedRules.scale?.x, // TODO: fix, typing tells us scale *is* defined
        bakedRules.scale?.y,
        bakedRules.padShape,
        bakedRules.size.x,
        bakedRules.size.y,
        bakedRules.cornerRadius?.topLeft,
        bakedRules.cornerRadius?.topRight,
        bakedRules.cornerRadius?.bottomLeft,
        bakedRules.cornerRadius?.bottomRight,
    ]);
}
function doShapeCreationFromBakedRules(bakedRules, expansion, options = {}) {
    // Important: Only svg shapes (and possibly other externally-defined shapes such as DXF, though that's
    // not implemented) should be scaled by the unit rule. Other values (eg rectangle size) are already
    // correctly unit-converted in the rules parsing/evaluation phase.
    const scaleUnit = scaleFromUnit(bakedRules.unit);
    const scale = new Vector2(("scale" in bakedRules ? bakedRules.scale?.x : 1) ?? 1, ("scale" in bakedRules ? bakedRules.scale?.y : 1) ?? 1);
    // If valid asset data is contained in bakedRules then attempt shape creation from that data - asset data takes
    // precedence in all cases
    if ("asset" in bakedRules && bakedRules.asset?.payloadFileName && bakedRules.asset?.payloadAsBase64) {
        if (isSvgFileName(bakedRules.asset.payloadFileName)) {
            try {
                return createShapeFromSvgString(atob(bakedRules.asset.payloadAsBase64), {
                    expansion,
                    scale: scale.multiplyScalar(scaleUnit),
                    polygonSegments: options.polygonSegments,
                });
            }
            catch (e) {
                if (options.logger && "captureError" in options.logger) {
                    options.logger?.captureError(e, { notifyUserMessage: "Failed to create shape from SVG string." });
                }
                return undefined;
            }
        }
        else if (isDxfFileName(bakedRules.asset.payloadFileName)) {
            try {
                return createShapeFromDxfString(atob(bakedRules.asset.payloadAsBase64), {
                    expansion,
                    scale,
                    center: false,
                });
            }
            catch (e) {
                if (options.logger && "captureError" in options.logger) {
                    options.logger?.captureError(e, { notifyUserMessage: "Failed to create shape from DXF string." });
                }
                return undefined;
            }
        }
    }
    if ("padShape" in bakedRules && bakedRules.padShape) {
        switch (bakedRules.padShape) {
            case PcbLayoutFootprintPadShape.circular: {
                return createCircleOrOblongShape(bakedRules.size, { x: 0, y: 0 }, 0);
            }
            case PcbLayoutFootprintPadShape.rectangle: {
                const shape = createRectangularShape(-bakedRules.size.x / 2, -bakedRules.size.y / 2, bakedRules.size.x, bakedRules.size.y, bakedRules.cornerRadius);
                if (typeof expansion !== "undefined") {
                    return Clipper.fromShape(shape, { closed: true })
                        .offset(expansion, { joinType: JoinType.Round })
                        .toShape(0);
                }
                return shape;
            }
            default: {
                return createSvgShape(bakedRules.padShape, undefined, { scale: scale.multiplyScalar(scaleUnit) });
            }
        }
    }
    if ("linePath" in bakedRules && bakedRules.linePath) {
        // Note that linePath currently only supports SVG-styled path data so for this rule, there is no need to check for the rectangle and
        // circle primitive cases (as with padShape and boardShape rules)
        return createSvgShape(bakedRules.linePath, undefined, { expansion, scale: scale.multiplyScalar(scaleUnit) });
    }
    if ("boardShape" in bakedRules && bakedRules.boardShape) {
        switch (bakedRules.boardShape) {
            case PcbLayoutShape.rectangle: {
                return createRectangularShape(-bakedRules.size.x / 2, -bakedRules.size.y / 2, bakedRules.size.x, bakedRules.size.y, bakedRules.cornerRadius);
            }
            case PcbLayoutShape.circular: {
                return createCircularShape(bakedRules.size.x / 2);
            }
            case PcbLayoutShape.flux: {
                return createFluxShape(bakedRules.size.x);
            }
            default: {
                return createSvgShape(bakedRules.boardShape, undefined, { scale: scale.multiplyScalar(scaleUnit) });
            }
        }
    }
    if ("zoneShape" in bakedRules && bakedRules.zoneShape) {
        switch (bakedRules.zoneShape) {
            case PcbLayoutShape.rectangle: {
                return createRectangularShape(-bakedRules.size.x / 2, -bakedRules.size.y / 2, bakedRules.size.x, bakedRules.size.y, bakedRules.cornerRadius);
            }
            case PcbLayoutFootprintPadShape.circular: {
                return createCircleOrOblongShape(bakedRules.size, { x: 0, y: 0 }, 0);
            }
            case PcbLayoutShape.flux: {
                return createFluxShape(bakedRules.size.x);
            }
            default: {
                return createSvgShape(bakedRules.zoneShape, undefined, { scale: scale.multiplyScalar(scaleUnit) });
            }
        }
    }
    if ("polygonShape" in bakedRules && bakedRules.polygonShape) {
        return createShapeFromPolygonBakedRules(bakedRules);
    }
    return undefined;
}
/**
 * Creates a Shape instance from rules and rule values of specified bakedRules, based on Flux's universal definition of rule
 * and rule value precedence. Returns undefined if unable to parse or extract shape from bakedRules parameter
 *
 * Precedence of rule processing for shape creation precedence is according to this order:
 * 1. Asset rule with valid svg base64 data
 * 2. Asset rule with valid dfx base64 data
 * 3. PadShape rule with circular, rectangular or svg string value
 * 4. LinePath rule with svg string value
 * 5. BoardShape rule with rectangular, circular, flux, or svg string value
 */
export function createShapeFromBakedRules(bakedRules, expansion, options = {}) {
    return cache(() => doShapeCreationFromBakedRules(bakedRules, expansion), "createShapeFromBakedRules", [
        bakedRules,
        expansion,
        options,
    ]);
}
// Why I don't like 'createShapeFromBakedRules'; it assumes shape generation logic should 'behave the same way' for calling code - where ever calling code
// requires "shapes from baked rules".
// On the surface, this seems reasonable. In practice, it doesn't always work out. For example, gerber export:
//
// Copper for Custom Pads is translated and rotated by an 'at' field of that KiCad pad.
// SolderMask for Custom Pads requires translation and rotation be baked into the custom geometry of that KiCad pad.
//
// Situations like this make it difficult for a "catch all" function like createShapeFromBakedRules to resolve shape geometry, per bespoke and difference in
// required behavior of calling code
export function createShapeForPad(params) {
    const scale = new Vector2(params.bakedRules.scale?.x ?? 1, params.bakedRules.scale?.y ?? 1);
    if (params.bakedRules.asset?.payloadFileName && params.bakedRules.asset?.payloadAsBase64) {
        if (isSvgFileName(params.bakedRules.asset.payloadFileName)) {
            return createSvgShape(atob(params.bakedRules.asset.payloadAsBase64), undefined, { scale });
        }
        if (isDxfFileName(params.bakedRules.asset.payloadFileName)) {
            return createShapeFromDxfString(atob(params.bakedRules.asset.payloadAsBase64), {
                scale,
                center: false,
            });
        }
    }
    switch (params.bakedRules.padShape) {
        case PcbLayoutFootprintPadShape.circular: {
            return createCircleOrOblongShape(params.bakedRules.size, { x: 0, y: 0 }, 0, params.segments);
        }
        case PcbLayoutFootprintPadShape.rectangle: {
            return createRectangularShape(-params.bakedRules.size.x / 2, -params.bakedRules.size.y / 2, params.bakedRules.size.x, params.bakedRules.size.y, params.bakedRules.cornerRadius);
        }
        default: {
            return createSvgShape(params.bakedRules.padShape, undefined, { scale });
        }
    }
}
/**
 * Creates a shape from polygon baked rules. Extracted from the main shape creation
 * function to support reuse in expression context area calculations.
 */
export function createShapeFromPolygonBakedRules(bakedRules) {
    switch (bakedRules.polygonShape) {
        case PcbLayoutShape.rectangle: {
            return createRectangularShape(-bakedRules.size.x / 2, -bakedRules.size.y / 2, bakedRules.size.x, bakedRules.size.y, bakedRules.cornerRadius);
        }
        case PcbLayoutFootprintPadShape.circular: {
            return createCircleOrOblongShape(bakedRules.size, { x: 0, y: 0 }, 0);
        }
        default: {
            // HACK: unaffected by scale for now
            return createSvgShape(bakedRules.polygonShape, undefined);
        }
    }
}
//# sourceMappingURL=createShapeFromBakedRules.js.map